import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '../../contexts/AuthContext';

// Calendario do recorte: pergunta ao DVR o que ele tem gravado no mes e so
// libera os dias com gravacao. No dia escolhido, uma barra de 24h mostra os
// trechos gravados — clicar nela (ou num trecho da lista) ja comeca a assistir.
//
// Equipamento sem essa API (ou fora do ar): o backend devolve
// `disponivel: false` e o calendario libera todos os dias, como antes.

const DIAS_SEMANA = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho',
  'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const p2 = (n) => String(n).padStart(2, '0');
const chaveDia = (d) => `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
const horaDe = (d) => `${p2(d.getHours())}:${p2(d.getMinutes())}:${p2(d.getSeconds())}`;

// "2026-10-06T13:00:00" -> Date local.
function lerLocal(texto) {
  const m = String(texto || '').match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]) : null;
}

// Dias (chave AAAA-MM-DD) tocados por algum periodo.
function diasComGravacao(periodos) {
  const dias = new Set();
  for (const p of periodos) {
    const d = new Date(p.inicio.getFullYear(), p.inicio.getMonth(), p.inicio.getDate());
    while (d <= p.fim) {
      dias.add(chaveDia(d));
      d.setDate(d.getDate() + 1);
    }
  }
  return dias;
}

// Periodos cortados ao dia escolhido.
function periodosDoDia(periodos, dia) {
  const ini = new Date(`${dia}T00:00:00`);
  const fim = new Date(ini.getTime() + 86400000);
  return periodos
    .filter((p) => p.fim > ini && p.inicio < fim)
    .map((p) => ({ inicio: p.inicio < ini ? ini : p.inicio, fim: p.fim > fim ? fim : p.fim }));
}

export default function CalendarioGravacoes({ camera, onAssistir }) {
  const agora = new Date();
  const [mes, setMes] = useState(() => new Date(agora.getFullYear(), agora.getMonth(), 1));
  const [dia, setDia] = useState(() => chaveDia(agora));
  // Ponto de partida padrao: 10 minutos atras.
  const [hora, setHora] = useState(() => horaDe(new Date(Date.now() - 10 * 60 * 1000)));
  const [dados, setDados] = useState(null); // { disponivel, completo, periodos: [{inicio: Date, fim: Date}] }
  const [carregando, setCarregando] = useState(false);

  const cameraId = camera?.id;
  useEffect(() => {
    if (!cameraId) return undefined;
    let cancelado = false;
    const ultimo = new Date(mes.getFullYear(), mes.getMonth() + 1, 0);
    setCarregando(true);
    axios.get(`${API}/cameras/recortes/gravacoes`, {
      params: { camera_id: cameraId, de: chaveDia(mes), ate: chaveDia(ultimo) },
    })
      .then((res) => {
        if (cancelado) return;
        const d = res.data?.data || {};
        setDados({
          disponivel: !!d.disponivel,
          completo: d.completo !== false,
          motivo: d.motivo || '',
          periodos: (d.periodos || [])
            .map((p) => ({ inicio: lerLocal(p.inicio), fim: lerLocal(p.fim) }))
            .filter((p) => p.inicio && p.fim),
        });
      })
      .catch(() => { if (!cancelado) setDados({ disponivel: false, completo: true, motivo: '', periodos: [] }); })
      .finally(() => { if (!cancelado) setCarregando(false); });
    return () => { cancelado = true; };
  }, [cameraId, mes]);

  const comGravacao = useMemo(() => diasComGravacao(dados?.periodos || []), [dados]);
  const doDia = useMemo(() => periodosDoDia(dados?.periodos || [], dia), [dados, dia]);
  const filtrando = !!dados?.disponivel;
  const hoje = chaveDia(agora);

  // Grade do mes: brancos ate o dia da semana do dia 1, depois os dias.
  const celulas = [];
  for (let i = 0; i < mes.getDay(); i += 1) celulas.push(null);
  const totalDias = new Date(mes.getFullYear(), mes.getMonth() + 1, 0).getDate();
  for (let n = 1; n <= totalDias; n += 1) celulas.push(new Date(mes.getFullYear(), mes.getMonth(), n));

  const mesSeguinte = new Date(mes.getFullYear(), mes.getMonth() + 1, 1);
  const podeAvancar = mesSeguinte <= agora;

  const escolherDia = (chave) => {
    setDia(chave);
    // Ja posiciona no primeiro trecho gravado do dia.
    const primeiro = periodosDoDia(dados?.periodos || [], chave)[0];
    if (primeiro) setHora(horaDe(primeiro.inicio));
  };

  const assistirEm = (d) => {
    const h = horaDe(d);
    setHora(h);
    onAssistir(`${dia}T${h}`);
  };

  // Clique na barra de 24h: vira horario; fora de um trecho gravado, pula
  // para o comeco do proximo (ou do ultimo antes dele).
  const clicarBarra = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const fracao = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    let alvo = new Date(new Date(`${dia}T00:00:00`).getTime() + Math.floor(fracao * 86400) * 1000);
    if (filtrando && doDia.length && !doDia.some((p) => alvo >= p.inicio && alvo < p.fim)) {
      const proximo = doDia.find((p) => p.inicio > alvo);
      alvo = proximo ? proximo.inicio : doDia[doDia.length - 1].inicio;
    }
    assistirEm(alvo);
  };

  const posicao = (d) => ((d - new Date(`${dia}T00:00:00`)) / 86400000) * 100;
  const fmt = (d) => `${p2(d.getHours())}:${p2(d.getMinutes())}`;

  return (
    <div className="cal-grav">
      <div className="cal-grav__topo">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => setMes(new Date(mes.getFullYear(), mes.getMonth() - 1, 1))}
          title="Mês anterior"
        >
          <i className="bi bi-chevron-left" />
        </button>
        <strong>{MESES[mes.getMonth()]} {mes.getFullYear()}</strong>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={!podeAvancar}
          onClick={() => setMes(mesSeguinte)}
          title="Próximo mês"
        >
          <i className="bi bi-chevron-right" />
        </button>
      </div>

      <div className={`cal-grav__grade${carregando ? ' cal-grav__grade--carregando' : ''}`}>
        {DIAS_SEMANA.map((s, i) => <span key={`s${i}`} className="cal-grav__semana">{s}</span>)}
        {celulas.map((d, i) => {
          if (!d) return <span key={`b${i}`} />;
          const chave = chaveDia(d);
          const futuro = chave > hoje;
          const tem = comGravacao.has(chave);
          const bloqueado = futuro || (filtrando && !tem);
          return (
            <button
              key={chave}
              type="button"
              disabled={bloqueado || carregando}
              className={[
                'cal-grav__dia',
                filtrando && tem ? 'cal-grav__dia--gravado' : '',
                chave === dia ? 'cal-grav__dia--ativo' : '',
                chave === hoje ? 'cal-grav__dia--hoje' : '',
              ].join(' ')}
              onClick={() => escolherDia(chave)}
              title={filtrando && !tem && !futuro ? 'Sem gravação neste dia' : undefined}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>

      {carregando ? <small className="cell-muted">Consultando o equipamento…</small> : null}
      {!carregando && dados && !dados.disponivel ? (
        <small className="cell-muted">
          Este equipamento não informou os horários gravados — todos os dias ficam liberados.
        </small>
      ) : null}
      {!carregando && dados?.disponivel && !dados.completo ? (
        <small className="cell-muted">Muitas gravações neste mês: a lista pode estar incompleta.</small>
      ) : null}

      {filtrando && !carregando ? (
        <>
          <div
            className="cal-grav__barra"
            onClick={clicarBarra}
            role="presentation"
            title="Clique para assistir a partir desse horário"
          >
            {doDia.map((p) => (
              <span
                key={p.inicio.getTime()}
                className="cal-grav__trecho"
                style={{ left: `${posicao(p.inicio)}%`, width: `${Math.max(0.4, posicao(p.fim) - posicao(p.inicio))}%` }}
              />
            ))}
          </div>
          <div className="cal-grav__escala"><span>00h</span><span>06h</span><span>12h</span><span>18h</span><span>24h</span></div>
          {doDia.length ? (
            <div className="cal-grav__trechos">
              {doDia.slice(0, 12).map((p) => (
                <button key={p.inicio.getTime()} type="button" className="cal-grav__chip" onClick={() => assistirEm(p.inicio)}>
                  {fmt(p.inicio)}–{fmt(p.fim)}
                </button>
              ))}
              {doDia.length > 12 ? <small className="cell-muted">+{doDia.length - 12} trechos (use a barra)</small> : null}
            </div>
          ) : (
            <small className="cell-muted">Sem gravação neste dia.</small>
          )}
        </>
      ) : null}

      <label className="portal-field">
        <span>Assistir a partir de</span>
        <input type="time" step={1} value={hora} onChange={(e) => setHora(e.target.value)} />
      </label>
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        disabled={!camera || !hora}
        onClick={() => onAssistir(`${dia}T${hora.length === 5 ? `${hora}:00` : hora}`)}
      >
        <i className="bi bi-play-fill" /> Assistir
      </button>
    </div>
  );
}
